import {useEffect, useState} from "react";
import {PublicationAiReviewApi, type PublicationAiReviewState, type PublicationAdvanceCommand, type PublicationSummary} from "@/api/publication";
import {RunJobs} from "@/api/runJobs";
import {Tier1ModelSelect, useTier1Model} from "@/components/Tier1ModelSelect";
import {useRunJobs} from "@/stores/runJobs";
import {getPublicationReadiness} from "@/utils/publicationState";

interface Props {
  publication: PublicationSummary;
  busy: boolean;
  onAdvance: (command: PublicationAdvanceCommand) => void | Promise<void>;
  onActiveChange: (active: boolean) => void;
}

export function automaticProgressText(state: PublicationAiReviewState | null): string | null {
  if (!state?.job_id) return null;
  if (state.phase === "dry_run") {
    return `Final Wikidata check: ${state.processed} / ${state.total} retained records`;
  }
  if (state.phase === "prepare_subset") return "Prepare the supported records";
  if (state.phase === "approve_subset") return "Save the automatic review";
  return `AI check: ${state.processed} / ${state.total} records`;
}

export function WikidataPublicationAiReview({publication, busy, onAdvance, onActiveChange}: Props) {
  const [state, setState] = useState<PublicationAiReviewState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [override, setOverride] = useState(false);
  const model = useTier1Model();
  const [verificationModel, setVerificationModel] = useState("");
  const active = state?.status === "queued" || state?.status === "running";
  const {run_id: runId, publication_id: publicationId, plan, approval_set: approval} = publication;
  const current = publication.source_current && getPublicationReadiness(publication).approvalCurrent && !publication.execution;
  const report = state?.report;
  const blockedCount = plan?.action_counts.blocked ?? 0;
  const reportCurrent = current && report?.publication_id === publicationId && report?.plan_id === plan?.plan_id
    && report?.plan_digest === plan?.plan_digest && report?.release_digest === publication.current_release.release_digest;
  const reportPreparedThisRelease = report?.automatic === true && report.result_publication_id === publicationId;
  const recommended = reportCurrent && state?.status === "succeeded"
    ? (report?.items ?? []).flatMap((item) => item.status === "recommended" && item.consent ? [item.consent] : []) : [];

  useEffect(() => {
    onActiveChange(active);
    return () => onActiveChange(false);
  }, [active, onActiveChange]);

  useEffect(() => {
    let stopped = false;
    void PublicationAiReviewApi.read(runId, publicationId).then((saved) => {
      if (!stopped) setState(saved);
    }).catch((caught: unknown) => {if (!stopped) setError(String(caught));})
      .finally(() => {if (!stopped) setLoading(false);});
    return () => {stopped = true;};
  }, [runId, publicationId]);

  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    let lastKey = "";
    const poll = async () => {
      try {
        const next = await PublicationAiReviewApi.read(runId, publicationId);
        if (stopped) return;
        // Re-render only when the summary actually changed: the 1850-row
        // publication panel must not repaint every 2 s (2026-10-13 lag).
        const nextKey = `${next.status}:${next.phase}:${next.processed}:${next.total}:${next.job_id ?? ""}`;
        if (nextKey !== lastKey) {
          lastKey = nextKey;
          setState(next);
        }
      } catch (caught: unknown) {if (!stopped) setError(String(caught));}
      if (!stopped) timer = setTimeout(() => {void poll();}, 5000);
    };
    timer = setTimeout(() => {void poll();}, 1000);
    return () => {stopped = true; clearTimeout(timer);};
  }, [active, runId, publicationId]);

  // Job updates arrive on the SSE tray (useProjectEvents → upsertJob);
  // do not poll RunJobs.get in a loop beside the summary poll.
  const jobId = state?.job_id;
  const job = useRunJobs((s) => (jobId ? s.jobs[jobId] : undefined));
  useEffect(() => {
    if (!job || !jobId) return;
    if (job.status === "queued" || job.status === "running") return;
    let stopped = false;
    void PublicationAiReviewApi.read(runId, publicationId).then((next) => {
      if (!stopped) setState(next);
    }).catch((caught: unknown) => {if (!stopped) setError(String(caught));});
    return () => {stopped = true;};
  }, [job?.status, jobId, runId, publicationId]);

  const start = async (automatic = false, retryFailed = false) => {
    if (!plan) return;
    setLoading(true);
    setError(null);
    try {
      const next = await PublicationAiReviewApi.start(runId, publicationId, {
        plan_id: plan.plan_id, plan_digest: plan.plan_digest, tier_model: model.tierModel, force_refresh: override && !retryFailed,
        ...(automatic ? {automatic: true, automatic_scope: "blocked" as const, verification_model: verificationModel || model.tierModel} : {}),
      });
      setState(next);
      if (next.job_id) useRunJobs.getState().upsertJob(await RunJobs.get(runId, next.job_id));
    } catch (caught: unknown) {setError(String(caught));}
    finally {setLoading(false);}
  };

  const approve = async () => {
    if (!approval || recommended.length === 0) return;
    await onAdvance({type: "dry_run", approval_set_id: approval.approval_set_id,
      expected_approval_digest: approval.approval_digest, force_refresh: true, foreign_qid_consents: recommended});
  };

  return <div className="rounded-lg border border-white/10 p-3 space-y-3" data-testid="publication-ai-review">
    <p className="font-medium">Resolve with AI</p>
    <p className="text-sm muted">The system checks the {blockedCount} record{blockedCount === 1 ? "" : "s"} that need attention. It keeps the checked records and defers an uncertain record. It does not publish.</p>
    <button type="button" className="button-primary text-sm" disabled={busy || active || loading || !current || !model.selected?.available}
      onClick={() => {void start(true, report?.automatic === true && report.items.some(item => item.resolution?.retryable));}}>
      {active ? "AI checks in progress" : report?.automatic && report.items.some(item => item.resolution?.retryable) ? "Retry unavailable sources" : `Resolve ${blockedCount} item${blockedCount === 1 ? "" : "s"} with AI`}
    </button>
    <details className="space-y-3">
      <summary className="cursor-pointer text-sm muted">Advanced AI tools</summary>
      <Tier1ModelSelect label="Assessment model" tierModel={model.tierModel} onChange={model.setTierModel} list={model.list}
        loading={model.loading} disabled={busy || active || loading} />
      <Tier1ModelSelect label="Verification model (independent second check)" tierModel={verificationModel || model.tierModel} onChange={setVerificationModel} list={model.list}
        loading={model.loading} disabled={busy || active || loading} />
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={override}
        disabled={busy || active || loading} onChange={(event) => setOverride(event.target.checked)} />Check AI results again</label>
      <p className="text-xs muted">The optional advisory review reports recommendations. It does not prepare or publish records.</p>
      <button type="button" className="button-ghost text-sm" disabled={busy || active || loading || !current || !model.selected?.available}
        onClick={() => {void start();}}>AI review blocked items</button>
    </details>
    {automaticProgressText(state) && <p className="text-xs" aria-live="polite">{automaticProgressText(state)}</p>}
    {state?.message && <p className="text-xs muted">{state.message}</p>}
    {active && state?.job_id && <button type="button" className="button-ghost text-sm"
      onClick={() => {if (state.job_id) void RunJobs.cancel(runId, state.job_id).catch((caught: unknown) => setError(String(caught)));}}>Cancel AI review</button>}
    {(error || state?.error) && <p className="text-danger text-sm">{error || state?.error}</p>}
    {report && <div data-testid="publication-ai-report" className="space-y-2">
      <p className="text-sm">Last result: {report.automatic ? "Automatic preparation" : "Advisory review only"}</p>
      <details><summary className="cursor-pointer text-sm">Full AI report</summary>
      <p className="text-xs muted">Report: {report.tier_model} · {report.created_at}</p>
      {!reportCurrent && !reportPreparedThisRelease && <p className="text-warn">This report does not match the current Release and Plan.</p>}
      <ul className="space-y-3">{report.items.map((item) => <li key={item.entity_key} className="text-sm">
        <p>{item.label} · {item.status.replaceAll("_", " ")}</p><p className="muted">{item.reason}</p>
        {item.qid && <a className="link-accent underline" target="_blank" rel="noopener noreferrer"
          href={`https://${publication.target === "live" ? "www" : "test"}.wikidata.org/wiki/${item.qid}`}>Review {item.qid}</a>}
      </li>)}</ul>
      {!report.automatic && <button type="button" className="button-primary text-sm" disabled={busy || active || loading || recommended.length === 0}
        onClick={() => {void approve();}}>Approve AI recommendations ({recommended.length}) and check again</button>}
      </details>
      {report.automatic && <div className="space-y-2">
        {reportPreparedThisRelease && <p className="text-xs muted">This Release uses the checked subset from this report.</p>}
        <p>{report.items.filter((item) => item.status === "reuse_existing").length} reused · {report.items.filter((item) => item.status === "update_existing").length} update candidates · {report.items.filter((item) => item.status === "create").length} create candidates · {report.items.filter((item) => item.status === "deferred").length} deferred</p>
        <p className="text-xs muted">Deferred items remain in this report and the Studio source. They are not approved or uploaded.</p>
        {state?.status === "succeeded" && (report.result_publication_id
          ? <a className="button-primary text-sm" href={`/runs/${encodeURIComponent(runId)}/wikidata-studio?publication=${encodeURIComponent(report.result_publication_id)}`}>Open prepared Release</a>
          : <p>No items passed the automatic policy. No new Release is ready.</p>)}
      </div>}
      {!report.automatic && <p className="text-xs muted">Uncertain items stay blocked. The fresh dry-run checks every consent against the current Wikidata revision.</p>}
    </div>}
  </div>;
}
